import mobileDoubleTriangle from '../../images/mobile/doubleTriangle.png'
import mobileCross          from '../../images/mobile/cross.png'
import EventEmitter         from '../Utils/EventEmitter'

export default class Controls extends EventEmitter
{
    constructor(_options)
    {
        super()

        this.config   = _options.config
        this.sizes    = _options.sizes
        this.time     = _options.time
        this.camera   = _options.camera
        this.sounds   = _options.sounds
        this.renderer = _options.renderer   // needed for XR gamepad polling

        // Set by World after Network is ready
        this.network = null

        this.setActions()
        this.setKeyboard()

        // Auto-init touch UI on touch-capable devices. The Application's
        // global touchstart handler used to do this on first tap, but that
        // tap fires on the title screen *before* World/Controls exists, so
        // `world?.controls.setTouch()` no-ops and the listener (`once: true`)
        // is consumed — leaving touch users with no on-screen controls.
        const touchCapable =
            ('ontouchstart' in window) ||
            (navigator.maxTouchPoints > 0) ||
            (navigator.msMaxTouchPoints > 0)

        if(touchCapable)
        {
            // Defer one frame so the DOM is settled and World has registered
            // its tick listeners (the joystick reads from this.time on tick).
            requestAnimationFrame(() => this.setTouch())
        }
    }

    setActions()
    {
        this.actions = {}
        this.actions.up = false
        this.actions.right = false
        this.actions.down = false
        this.actions.left = false
        this.actions.brake = false
        this.actions.boost = false

        // Edge-detection state for XR tap buttons (A = jump, B = fire)
        this._xrAWasPressed = false
        this._xrBWasPressed = false

        document.addEventListener('visibilitychange', () =>
        {
            if(!document.hidden)
            {
                this.actions.up = false
                this.actions.right = false
                this.actions.down = false
                this.actions.left = false
                this.actions.brake = false
                this.actions.boost = false
                this._sendInput()
            }
        })

        // Poll XR controller gamepads every frame when presenting
        this.time.on('tick', () =>
        {
            if(this.renderer?.xr?.isPresenting) this._pollXRGamepad()
        })
    }

    // ── Meta Quest controller → actions ──────────────────────────────────
    // Left thumbstick: X = steer, Y = gas/reverse
    // Left trigger  : brake
    // Right trigger : boost
    // Right A button: jump (tap)
    // Right B button: fire (tap, combat mode)
    _pollXRGamepad()
    {
        const session = this.renderer.xr.getSession()
        if(!session) return

        const DZ = 0.2   // thumbstick deadzone

        let up = false, down = false, left = false, right = false
        let brake = false, boost = false
        let aPressed = false, bPressed = false

        for(const source of session.inputSources)
        {
            const gp = source.gamepad
            if(!gp) continue

            if(source.handedness === 'left')
            {
                const sx = gp.axes[2] ?? 0
                const sy = gp.axes[3] ?? 0
                if(sx < -DZ) left  = true
                if(sx >  DZ) right = true
                if(sy < -DZ) up    = true
                if(sy >  DZ) down  = true
                if(gp.buttons[0]?.pressed) brake = true
            }

            if(source.handedness === 'right')
            {
                if(gp.buttons[0]?.pressed)  boost    = true
                if(gp.buttons[4]?.pressed)  aPressed = true
                if(gp.buttons[5]?.pressed)  bPressed = true
            }
        }

        // Fire one-shot events on press edge (not held)
        if(aPressed && !this._xrAWasPressed) this.trigger('action', ['jump'])
        if(bPressed && !this._xrBWasPressed) this.trigger('action', ['fire'])
        this._xrAWasPressed = aPressed
        this._xrBWasPressed = bPressed

        const changed =
            up    !== this.actions.up    ||
            down  !== this.actions.down  ||
            left  !== this.actions.left  ||
            right !== this.actions.right ||
            brake !== this.actions.brake ||
            boost !== this.actions.boost

        this.actions.up    = up
        this.actions.down  = down
        this.actions.left  = left
        this.actions.right = right
        this.actions.brake = brake
        this.actions.boost = boost

        if(changed) this._sendInput()
    }

    _sendInput()
    {
        if(this.network)
        {
            this.network.sendInput({ ...this.actions })
        }
    }

    setKeyboard()
    {
        this.keyboard = {}
        this.keyboard.events = {}

        this.keyboard.events.keyDown = (_event) =>
        {
            // Don't steal keys from text inputs (chat, lobby forms)
            const tag = document.activeElement?.tagName
            if(tag === 'INPUT' || tag === 'TEXTAREA') return

            switch(_event.code)
            {
                case 'ArrowUp':
                case 'KeyW':
                    this.camera.pan.reset()
                    this.actions.up = true
                    this._sendInput()
                    break

                case 'ArrowRight':
                case 'KeyD':
                    this.actions.right = true
                    this._sendInput()
                    break

                case 'ArrowDown':
                case 'KeyS':
                    this.camera.pan.reset()
                    this.actions.down = true
                    this._sendInput()
                    break

                case 'ArrowLeft':
                case 'KeyA':
                    this.actions.left = true
                    this._sendInput()
                    break

                case 'ControlLeft':
                case 'ControlRight':
                case 'KeyX':
                    this.actions.brake = true
                    this._sendInput()
                    break

                case 'Space':
                    // Tap action — handled by World, not held.
                    // preventDefault stops the browser from scrolling on space.
                    _event.preventDefault()
                    this.trigger('action', ['jump'])
                    break

                case 'ShiftLeft':
                case 'ShiftRight':
                    this.actions.boost = true
                    this._sendInput()
                    break

                case 'KeyF':
                    this.trigger('action', ['fire'])
                    break
            }
        }

        this.keyboard.events.keyUp = (_event) =>
        {
            switch(_event.code)
            {
                case 'ArrowUp':
                case 'KeyW':
                    this.actions.up = false
                    this._sendInput()
                    break

                case 'ArrowRight':
                case 'KeyD':
                    this.actions.right = false
                    this._sendInput()
                    break

                case 'ArrowDown':
                case 'KeyS':
                    this.actions.down = false
                    this._sendInput()
                    break

                case 'ArrowLeft':
                case 'KeyA':
                    this.actions.left = false
                    this._sendInput()
                    break

                case 'ControlLeft':
                case 'ControlRight':
                case 'KeyX':
                    this.actions.brake = false
                    this._sendInput()
                    break

                case 'ShiftLeft':
                case 'ShiftRight':
                    this.actions.boost = false
                    this._sendInput()
                    break

                case 'KeyR':
                    this.trigger('action', ['reset'])
                    break
            }
        }

        document.addEventListener('keydown', this.keyboard.events.keyDown)
        document.addEventListener('keyup', this.keyboard.events.keyUp)
    }

    setTouch()
    {
        if(this.touch?.$root) return        // idempotent — only build once

        this.touch = {}

        this.touch.$root = document.createElement('div')
        this.touch.$root.id = 'rl-touch-controls'
        document.body.appendChild(this.touch.$root)

        // Refcount sets for actions that multiple sources can press
        // (joystick + forward/backward/boost buttons).
        this._upHeldBy   = new Set()
        this._downHeldBy = new Set()

        this._buildTouchJoystick()
        this._buildTouchButtons()

        // Fade in once the countdown completes (called from World.setReveal)
        this.touch.reveal = () =>
        {
            this.touch.$root.classList.add('visible')
        }

        // Combat mode unhides the Fire button (called from World.setCombat)
        this.touch.showFire = () =>
        {
            if(this.touch.fire) this.touch.fire.style.display = ''
        }
    }

    // ── 2-axis joystick → actions.up/down/left/right ──────────────────────
    _buildTouchJoystick()
    {
        const joy = this.touch.joystick = {
            active:   false,
            touchId:  null,
            current:  { x: 0, y: 0 },
            origin:   { x: 0, y: 0 },   // where the finger first landed
            lastUp:   false,             // Y-direction at last release (for re-touch inheritance)
            lastDown: false,
        }

        const $j = document.createElement('div')
        $j.className = 'rl-touch-joystick'
        $j.innerHTML = `
            <div class="rl-touch-joystick-ring"></div>
            <div class="rl-touch-joystick-cursor"></div>
        `
        this.touch.$root.appendChild($j)
        joy.$element = $j
        joy.$cursor  = $j.querySelector('.rl-touch-joystick-cursor')

        // 2-axis steering: X = left/right, Y = forward/back.
        //
        // Deflection is measured from where the FINGER first landed, not
        // the geometric center of the element. That way casual contact
        // anywhere inside the joystick is treated as neutral — only an
        // intentional drag from the touch origin counts as input. Fixes
        // the case where the player's thumb naturally lands in the lower
        // half of the joystick and the car immediately reverses.
        this.time.on('tick', () =>
        {
            if(!joy.active) return

            const dx = joy.current.x - joy.origin.x
            const dy = joy.current.y - joy.origin.y
            const DZ = 14   // deadzone in px (relative to touch origin)

            const left  = dx < -DZ
            const right = dx >  DZ
            const up    = dy < -DZ
            const down  = dy >  DZ

            // Refcount: only update up/down when outside the vertical deadzone.
            // While the thumb stays inside the deadzone the last Y-direction
            // is preserved, preventing accidental gas cuts from micro-drift.
            if(up || down)
            {
                if(up)   this._upHeldBy.add('joystick');   else this._upHeldBy.delete('joystick')
                if(down) this._downHeldBy.add('joystick'); else this._downHeldBy.delete('joystick')
            }
            const newUp   = this._upHeldBy.size   > 0
            const newDown = this._downHeldBy.size > 0

            const changed =
                left   !== this.actions.left  ||
                right  !== this.actions.right ||
                newUp  !== this.actions.up    ||
                newDown !== this.actions.down

            this.actions.left  = left
            this.actions.right = right
            this.actions.up    = newUp
            this.actions.down  = newDown

            if(changed) this._sendInput()

            // Cursor visualization: also relative to origin so the visual
            // matches where the user feels their thumb has moved
            const dist = Math.min(Math.hypot(dx, dy), 50)
            const ang  = Math.atan2(dy, dx)
            joy.$cursor.style.transform =
                `translate(${Math.cos(ang) * dist}px, ${Math.sin(ang) * dist}px)`
        })

        // Touchstart: snapshot the landing point as the new origin so the
        // first frame doesn't read any deflection from a casual contact.
        $j.addEventListener('touchstart', (e) =>
        {
            e.preventDefault()
            if(joy.active) return   // ignore stray second touch on the joystick area
            const t = e.changedTouches[0]
            if(!t) return
            joy.active    = true
            joy.touchId   = t.identifier
            joy.origin.x  = t.clientX
            joy.origin.y  = t.clientY
            joy.current.x = t.clientX
            joy.current.y = t.clientY
            // Restore the direction from the previous touch so a brief
            // lift-and-re-touch doesn't cut the gas for even one frame.
            if(joy.lastUp)   this._upHeldBy.add('joystick')
            if(joy.lastDown) this._downHeldBy.add('joystick')
            this.camera?.pan?.reset?.()
        }, { passive: false })

        // …but track move + release on document so the finger can wander
        document.addEventListener('touchmove', (e) =>
        {
            if(!joy.active) return
            const t = [...e.changedTouches].find(tc => tc.identifier === joy.touchId)
            if(!t) return
            e.preventDefault()
            joy.current.x = t.clientX
            joy.current.y = t.clientY
        }, { passive: false })

        const releaseJoy = (e) =>
        {
            if(!joy.active) return
            const t = [...e.changedTouches].find(tc => tc.identifier === joy.touchId)
            if(!t) return
            joy.active  = false
            joy.touchId = null
            joy.$cursor.style.transform = 'translate(0,0)'

            // Remember Y-direction for the next re-touch, then clear.
            joy.lastUp   = this._upHeldBy.has('joystick')
            joy.lastDown = this._downHeldBy.has('joystick')
            this._upHeldBy.delete('joystick')
            this._downHeldBy.delete('joystick')
            const newUp   = this._upHeldBy.size   > 0
            const newDown = this._downHeldBy.size > 0

            let changed = false
            if(this.actions.left)              { this.actions.left  = false; changed = true }
            if(this.actions.right)             { this.actions.right = false; changed = true }
            if(this.actions.up   !== newUp)    { this.actions.up    = newUp;  changed = true }
            if(this.actions.down !== newDown)  { this.actions.down  = newDown; changed = true }
            if(changed) this._sendInput()
        }
        document.addEventListener('touchend',    releaseJoy)
        document.addEventListener('touchcancel', releaseJoy)
    }

    // ── Right-thumb action buttons ───────────────────────────────────────
    // Bottom-up: brake · boost · jump · fire
    _buildTouchButtons()
    {
        // Hold-style button with a PNG icon
        const makeIconHold = (id, iconUrl, iconRotate, slot, onPress, onRelease, accent) =>
        {
            const $b = document.createElement('button')
            $b.className = 'rl-touch-btn rl-touch-btn-icon'
            $b.id = `rl-touch-${id}`
            $b.dataset.slot = String(slot)
            if(accent) $b.dataset.accent = accent

            const $img = document.createElement('img')
            $img.src = iconUrl
            $img.alt = id
            $img.draggable = false
            if(iconRotate) $img.style.transform = `rotate(${iconRotate}deg)`
            $b.appendChild($img)

            this.touch.$root.appendChild($b)

            $b.addEventListener('touchstart', (e) =>
            {
                e.preventDefault()
                $b.classList.add('active')
                onPress()
            }, { passive: false })

            const release = () => { $b.classList.remove('active'); onRelease() }
            $b.addEventListener('touchend',    release)
            $b.addEventListener('touchcancel', release)
            return $b
        }

        // Tap-style button with a text label
        const makeTextTap = (id, label, slot, onTap, accent, hidden = false) =>
        {
            const $b = document.createElement('button')
            $b.className = 'rl-touch-btn rl-touch-btn-text'
            $b.id = `rl-touch-${id}`
            $b.dataset.slot = String(slot)
            if(accent) $b.dataset.accent = accent
            if(hidden) $b.style.display = 'none'
            $b.textContent = label
            this.touch.$root.appendChild($b)

            $b.addEventListener('touchstart', (e) =>
            {
                e.preventDefault()
                $b.classList.add('active')
                window.setTimeout(() => $b.classList.remove('active'), 120)
                onTap()
            }, { passive: false })
            return $b
        }

        // Slot 0 (bottom): brake — cross
        this.touch.brake = makeIconHold('brake', mobileCross, 0, 0,
            () => { this.actions.brake = true;  this._sendInput() },
            () => { this.actions.brake = false; this._sendInput() })

        // Slot 1: boost — double triangle
        this.touch.boost = makeIconHold('boost', mobileDoubleTriangle, 0, 1,
            () =>
            {
                this._upHeldBy.add('boost')
                this.actions.boost = true
                this.camera?.pan?.reset?.()
                this._sendInput()
            },
            () =>
            {
                this._upHeldBy.delete('boost')
                this.actions.boost = false
                this._sendInput()
            },
            'amber')

        // Slot 2: jump — text label, cyan accent
        this.touch.jump = makeTextTap('jump', 'JUMP', 2,
            () => this.trigger('action', ['jump']),
            'cyan')

        // Slot 3: fire — text label, redline accent, combat-mode only
        this.touch.fire = makeTextTap('fire', 'FIRE', 3,
            () => this.trigger('action', ['fire']),
            'redline',
            true)
    }
}
